import { Box, Typography } from "@mui/material";
import React from "react";

export interface LabelValueRow {
  label: string;
  // What stands under the label. A row whose value is null, undefined,
  // false or an empty string is left out.
  value: React.ReactNode;
  // Whether the row runs across both columns
  wide?: boolean;
}

export interface LabelValueGridProps {
  rows: LabelValueRow[];
}

const isShown = (value: React.ReactNode): boolean =>
  value !== null && value !== undefined && value !== false && value !== "";

// A label above its value: small capitals in the quieter colour. Shared with
// the grouped relation list, so that both read alike.
export const detailLabelStyle = {
  display: "block",
  color: "text.secondary",
  fontSize: "0.72rem",
  fontWeight: 500,
  letterSpacing: "0.06em",
  lineHeight: 1.5,
  textTransform: "uppercase",
} as const;

// A value beneath its label; a link in it stands out by its weight
export const detailValueStyle = {
  fontSize: "0.9375rem",
  lineHeight: 1.45,
  "& a": { fontWeight: 500 },
} as const;

/**
 * Labels with their values in two columns; a wide row takes both.
 * Renders nothing when no row has a value.
 */
const LabelValueGrid: React.FC<LabelValueGridProps> = ({ rows }) => {
  const shown = rows.filter((row) => isShown(row.value));
  if (shown.length === 0) {
    return null;
  }

  return (
    <Box
      component="dl"
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        columnGap: 3,
        rowGap: 2,
        m: 0,
      }}
    >
      {shown.map((row) => (
        <Box key={row.label} sx={{ gridColumn: row.wide ? "1 / -1" : "auto" }}>
          <Typography
            component="dt"
            variant="caption"
            sx={{ ...detailLabelStyle, mb: 0.25 }}
          >
            {row.label}
          </Typography>
          <Typography
            component="dd"
            variant="body2"
            sx={{ ...detailValueStyle, m: 0, overflowWrap: "anywhere" }}
          >
            {row.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export default LabelValueGrid;
